import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import '@awesome.me/webawesome/dist/components/button/button.js';
import '@awesome.me/webawesome/dist/components/callout/callout.js';
import '@awesome.me/webawesome/dist/components/dialog/dialog.js';
import '@awesome.me/webawesome/dist/components/input/input.js';
import '@awesome.me/webawesome/dist/components/option/option.js';
import '@awesome.me/webawesome/dist/components/select/select.js';
import '@awesome.me/webawesome/dist/components/spinner/spinner.js';

import { ApiError, api, type BillingConfig, type BillingGroup, type EverhourRule } from '../api.js';
import { pageStyles } from '../shared-styles.js';

/**
 * Billing groups (invoice sections) and Everhour routing rules of one client.
 * <billing-groups-dialog></billing-groups-dialog>, then dialog.show(clientId, name).
 */
@customElement('billing-groups-dialog')
export class BillingGroupsDialog extends LitElement {
  static styles = pageStyles;

  @state() private isOpen = false;
  @state() private clientId = 0;
  @state() private clientName = '';
  @state() private loading = false;
  @state() private saving = false;
  @state() private error = '';
  @state() private groups: BillingGroup[] = [];
  @state() private rules: EverhourRule[] = [];

  async show(clientId: number, clientName: string) {
    this.clientId = clientId;
    this.clientName = clientName;
    this.error = '';
    this.groups = [];
    this.rules = [];
    this.isOpen = true;
    this.loading = true;
    try {
      const cfg = await api.get<BillingConfig>(`/api/clients/${clientId}/billing`);
      this.groups = cfg.groups;
      this.rules = cfg.rules;
    } catch (e) {
      this.error = (e as Error).message;
    } finally {
      this.loading = false;
    }
  }

  private onHidden = (e: Event) => {
    if (e.target === e.currentTarget) this.isOpen = false;
  };

  private setGroupName(i: number, name: string) {
    this.groups = this.groups.map((g, j) => (j === i ? { ...g, name } : g));
  }

  private addGroup() {
    this.groups = [...this.groups, { id: null, name: '' }];
  }

  private removeGroup(i: number) {
    const removed = this.groups[i];
    this.groups = this.groups.filter((_, j) => j !== i);
    // Rules pointing at the removed group fall back to "ignore".
    this.rules = this.rules.map((r) => (r.group === removed.name ? { ...r, group: null } : r));
  }

  private moveGroup(i: number, delta: number) {
    const j = i + delta;
    if (j < 0 || j >= this.groups.length) return;
    const next = [...this.groups];
    [next[i], next[j]] = [next[j], next[i]];
    this.groups = next;
  }

  private setRule(i: number, patch: Partial<EverhourRule>) {
    this.rules = this.rules.map((r, j) => (j === i ? { ...r, ...patch } : r));
  }

  private addRule(kind: EverhourRule['kind']) {
    this.rules = [...this.rules, { kind, attribute: kind === 'attribute' ? 'Finance' : null, value: null, workspace: null, group: null }];
  }

  private removeRule(i: number) {
    this.rules = this.rules.filter((_, j) => j !== i);
  }

  private moveRule(i: number, delta: number) {
    const j = i + delta;
    if (j < 0 || j >= this.rules.length) return;
    const next = [...this.rules];
    [next[i], next[j]] = [next[j], next[i]];
    this.rules = next;
  }

  private async save() {
    this.saving = true;
    this.error = '';
    try {
      const cfg = await api.put<BillingConfig>(`/api/clients/${this.clientId}/billing`, { groups: this.groups, rules: this.rules });
      this.groups = cfg.groups;
      this.rules = cfg.rules;
      this.isOpen = false;
      this.dispatchEvent(new CustomEvent('saved', { bubbles: true, composed: true }));
    } catch (e) {
      if (e instanceof ApiError) {
        const hint = Object.values(e.fields)[0];
        this.error = hint ? `${e.message}: ${hint}` : e.message;
      } else {
        this.error = (e as Error).message;
      }
    } finally {
      this.saving = false;
    }
  }

  render() {
    const groupNames = this.groups.map((g) => g.name).filter(Boolean);
    return html`
      <wa-dialog label=${`Billing groups · ${this.clientName}`} ?open=${this.isOpen} @wa-after-hide=${this.onHidden} style="--width: min(60rem, calc(100vw - 1.5rem));">
        ${this.loading ? html`<wa-spinner></wa-spinner>` : nothing}
        ${this.error ? html`<wa-callout variant="danger">${this.error}</wa-callout>` : nothing}
        <p class="muted">
          Groups are the sections of this client's invoice. Imported hours are routed by the rules below, in order: attribute rules match a
          task attribute; workspace rules match the project's Everhour workspace and only apply to Finance = Billable or Internal. A rule
          with group "ignore" skips that match. Anything unmatched uses the contract's default group or stays unresolved.
        </p>

        <h4>Groups</h4>
        <div class="table-wrap"><table class="editable-table">
          <tbody>
            ${this.groups.map(
              (g, i) => html`
                <tr>
                  <td><wa-input size="small" .value=${g.name} placeholder="CITY" @input=${(e: Event) => this.setGroupName(i, (e.target as HTMLInputElement).value)}></wa-input></td>
                  <td class="actions">
                    <wa-button size="small" appearance="plain" ?disabled=${i === 0} @click=${() => this.moveGroup(i, -1)}>↑</wa-button>
                    <wa-button size="small" appearance="plain" ?disabled=${i === this.groups.length - 1} @click=${() => this.moveGroup(i, 1)}>↓</wa-button>
                    <wa-button size="small" appearance="plain" variant="danger" @click=${() => this.removeGroup(i)}>Remove</wa-button>
                  </td>
                </tr>
              `,
            )}
          </tbody>
        </table></div>
        <wa-button size="small" appearance="outlined" @click=${this.addGroup}>Add group</wa-button>

        <h4>Rules</h4>
        <div class="table-wrap"><table class="editable-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Kind</th>
              <th>Match</th>
              <th>Group</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            ${this.rules.map(
              (r, i) => html`
                <tr>
                  <td class="muted">${i + 1}</td>
                  <td>
                    <wa-select size="small" .value=${r.kind} @change=${(e: Event) => this.setRule(i, { kind: (e.target as HTMLSelectElement).value as EverhourRule['kind'] })}>
                      <wa-option value="attribute">attribute</wa-option>
                      <wa-option value="workspace">workspace</wa-option>
                    </wa-select>
                  </td>
                  <td>
                    ${r.kind === 'attribute'
                      ? html`
                          <wa-input size="small" placeholder="Finance" .value=${r.attribute ?? ''} @input=${(e: Event) => this.setRule(i, { attribute: (e.target as HTMLInputElement).value })}></wa-input>
                          =
                          <wa-input size="small" placeholder="CITY" .value=${r.value ?? ''} @input=${(e: Event) => this.setRule(i, { value: (e.target as HTMLInputElement).value })}></wa-input>
                        `
                      : html`<wa-input size="small" placeholder="ATOS Road Transport" .value=${r.workspace ?? ''} @input=${(e: Event) => this.setRule(i, { workspace: (e.target as HTMLInputElement).value })}></wa-input>`}
                  </td>
                  <td>
                    <wa-select size="small" .value=${r.group ?? ''} @change=${(e: Event) => this.setRule(i, { group: (e.target as HTMLSelectElement).value || null })}>
                      <wa-option value="">ignore</wa-option>
                      ${groupNames.map((name) => html`<wa-option value=${name}>${name}</wa-option>`)}
                    </wa-select>
                  </td>
                  <td class="actions">
                    <wa-button size="small" appearance="plain" ?disabled=${i === 0} @click=${() => this.moveRule(i, -1)}>↑</wa-button>
                    <wa-button size="small" appearance="plain" ?disabled=${i === this.rules.length - 1} @click=${() => this.moveRule(i, 1)}>↓</wa-button>
                    <wa-button size="small" appearance="plain" variant="danger" @click=${() => this.removeRule(i)}>Remove</wa-button>
                  </td>
                </tr>
              `,
            )}
          </tbody>
        </table></div>
        <wa-button size="small" appearance="outlined" @click=${() => this.addRule('attribute')}>Add attribute rule</wa-button>
        <wa-button size="small" appearance="outlined" @click=${() => this.addRule('workspace')}>Add workspace rule</wa-button>

        <div slot="footer" class="footer">
          <wa-button appearance="outlined" @click=${() => (this.isOpen = false)}>Cancel</wa-button>
          <wa-button variant="brand" ?loading=${this.saving} @click=${this.save}>Save</wa-button>
        </div>
      </wa-dialog>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'billing-groups-dialog': BillingGroupsDialog;
  }
}
